import React, { useState, useEffect } from 'react'
import { withAllContexts } from '../../../AllContextsWrapper';
import { History as HistoryIcon, ArrowRight, User, Calendar, Clock, CheckCircle } from 'lucide-react';
import CircularProgress from '@mui/material/CircularProgress';
import moment from 'moment';

const HistoryPart = ({ date, author, valuesChanged, isLast, app }) => {
    const isCamping = app.state.activeCalendar?.type === 'accommodation';
    const primaryBg = isCamping ? 'bg-taborisko-pine' : 'bg-splavma-teal';
    const primaryBorder = isCamping ? 'border-taborisko-pine' : 'border-splavma-teal';
    const primaryText = isCamping ? 'text-taborisko-pine' : 'text-splavma-teal';
    const primaryLightBg = isCamping ? 'bg-taborisko-pine/10' : 'bg-splavma-teal/10';

    return (
        <div className="relative pl-8 pb-8 last:pb-0">
            {/* Timeline Line */}
            {!isLast && (
                <div className="absolute top-4 left-3.5 h-full w-0.5 bg-gray-200"></div>
            )}

            {/* Timeline Dot */}
            <div className={`absolute top-1.5 left-1 w-5 h-5 rounded-full bg-white border-2 flex items-center justify-center z-10 ${primaryBorder}`}>
                <div className={`w-2 h-2 rounded-full ${primaryBg}`}></div>
            </div>

            {/* Content */}
            <div className="bg-white border border-gray-100 shadow-sm p-5 transition-all hover:shadow">
                {/* Header */}
                <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-4 pb-3 border-b border-gray-100">
                    <div className="flex items-center gap-2 text-gray-500 text-sm">
                        <Calendar className="w-4 h-4" />
                        <span className="font-bold">{date.split(' ')[0]}</span>
                        <Clock className="w-4 h-4 ml-2" />
                        <span className="font-medium">{date.split(' ')[1]}</span>
                    </div>
                    <div className="flex items-center gap-2">
                        <div className={`px-3 py-1 rounded-lg text-xs font-bold uppercase tracking-wider flex items-center gap-1.5 ${primaryLightBg} ${primaryText}`}>
                            <User className="w-3.5 h-3.5" />
                            {author || 'Neznámy'}
                        </div>
                    </div>
                </div>

                {/* Changes */}
                <div className="space-y-3">
                    {valuesChanged.map((v, idx) => {
                        const isCreation = v.name === "Vytvorená rezervácia";
                        const fieldName = app.functions.getDisplayText ? app.functions.getDisplayText(v.name) : v.name;

                        // Formatting values
                        let displayValue = v.value;
                        let displayNewValue = v.newValue;

                        if (displayValue === null || displayValue === undefined || displayValue === '') displayValue = <span className="text-gray-400 italic">Prázdne</span>;
                        if (displayNewValue === null || displayNewValue === undefined || displayNewValue === '') displayNewValue = <span className="text-gray-400 italic">Prázdne</span>;

                        // Try to translate if possible
                        if (app.functions.getDisplayText && typeof displayValue === 'string') displayValue = app.functions.getDisplayText(displayValue);
                        if (app.functions.getDisplayText && typeof displayNewValue === 'string') displayNewValue = app.functions.getDisplayText(displayNewValue);

                        return (
                            <div key={idx} className="flex flex-col gap-1">
                                <span className="text-xs font-semibold text-gray-500 uppercase tracking-wider">
                                    {fieldName}
                                </span>

                                {isCreation ? (
                                    <div className="flex items-center gap-2 text-success-600 bg-success-50 px-3 py-2 rounded-lg text-sm font-medium">
                                        <CheckCircle className="w-4 h-4" />
                                        Rezervácia vytvorená (ID: {v.value})
                                    </div>
                                ) : (
                                    <div className="flex items-center gap-3 bg-gray-50 p-2 rounded-lg text-sm group">
                                        <div className="flex-1 text-red-600 font-medium break-all line-through opacity-75">
                                            {displayValue}
                                        </div>
                                        <ArrowRight className="w-4 h-4 text-gray-400 flex-shrink-0" />
                                        <div className="flex-1 text-green-600 font-medium break-all">
                                            {displayNewValue}
                                        </div>
                                    </div>
                                )}
                            </div>
                        );
                    })}
                </div>
            </div>
        </div>
    );
};

const History = ({ reservation, app, socketIO }) => {
    const [history, setHistory] = useState([]);
    const [isLoading, setIsLoading] = useState(false);

    const formatDate = (date) => {
        if (!date) return null;
        return moment(date).format('YYYY-MM-DD');
    };

    const formatDateTime = (date) => {
        if (!date) return '-----';
        return moment(date).format('DD.MM.YYYY HH:mm');
    };

    const getHistory = () => {
        if (!reservation) return;
        setIsLoading(true);
        socketIO.socket.emit('reservationHistory:getAllByReservationId', reservation.id, (response) => {
            if (response.success) {
                setHistory(response.data);
            } else {
                setHistory([]);
            }
            setIsLoading(false);
        });
    };

    useEffect(() => {
        getHistory();
    }, [reservation.id, reservation.updatedAt]);

    let content = [];

    if (history) {
        // Group by created_at (to bundle multiple field changes into one "action")
        const grouped = {};

        history.forEach((log) => {
            // Create a key based on timestamp (minute precision sufficient?) and user
            const timeKey = moment(log.created_at).format('YYYY-MM-DD HH:mm:ss');
            const key = timeKey + '_' + (String(log.changed_by) || 'unknown');

            if (!grouped[key]) {
                grouped[key] = {
                    date: formatDateTime(log.created_at),
                    author: log.changed_by ? (socketIO.usersShort[log.changed_by] || 'Neznámy') : 'Neznámy',
                    valuesChanged: []
                };
            }

            let displayVal = log.old_value;
            let displayNewVal = log.new_value;

            if (log.field_name === 'start_date' || log.field_name === 'end_date') {
                displayVal = formatDate(displayVal);
                displayNewVal = formatDate(displayNewVal);
            }

            grouped[key].valuesChanged.push({
                name: log.field_name,
                value: displayVal,
                newValue: displayNewVal
            });
        });

        // Convert to array and sort DESC
        content = Object.keys(grouped)
            .sort((a, b) => b.localeCompare(a)) // Sort by string key (date desc)
            .map(key => ({
                key: key,
                ...grouped[key]
            }));
    }

    return (
        <div className="relative">
            {isLoading && (
                <div className="absolute inset-0 bg-white/50 backdrop-blur-sm flex items-center justify-center z-20 rounded-xl">
                    <CircularProgress size={40} className="text-primary-500" />
                </div>
            )}

            <div className="pb-8">
                {content.length === 0 && !isLoading ? (
                    <div className="flex flex-col items-center justify-center py-12 text-gray-400">
                        <HistoryIcon className="w-12 h-12 mb-3 opacity-20" />
                        <p className="font-medium">Žiadna história zmien</p>
                    </div>
                ) : (
                    content.map((action, idx) => (
                        <HistoryPart 
                            key={action.key}
                            {...action} 
                            isLast={idx === content.length - 1}
                            app={app}
                        />
                    ))
                )}

                {/* Always show creation at bottom if not already in history */}
                <HistoryPart 
                    key="creation"
                    date={formatDateTime(reservation.createdAt)}
                    author="Systém"
                    valuesChanged={[{ name: "Vytvorená rezervácia", value: reservation.id, newValue: '' }]}
                    isLast={true}
                    app={app}
                />
            </div>
        </div>
    );
};

export default withAllContexts(History);
